import Phaser from 'phaser';

export class ParticleManager {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * 드릴 효과 - 높은 층을 파고들 때
   */
  public drill(x: number, y: number) {
    // 작은 파티클 효과
    const particles = [];
    for (let i = 0; i < 3; i++) {
      const particle = this.scene.add.circle(
        x + Phaser.Math.Between(-10, 10),
        y + Phaser.Math.Between(-10, 10),
        Phaser.Math.Between(2, 4),
        0x8B4513 // 갈색 (흙 느낌)
      );
      particle.setAlpha(0.8);
      particles.push(particle);

      this.scene.tweens.add({
        targets: particle,
        alpha: 0,
        y: particle.y + 20,
        scale: 0.5,
        duration: 300,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  /**
   * 폭발 효과 - 플레이어 사망 시
   */
  public explode(x: number, y: number, color: number = 0xFF4757) {
    const particleCount = 12;

    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const speed = Phaser.Math.Between(50, 150);

      const particle = this.scene.add.circle(x, y, 6, color);
      particle.setDepth(100);

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scale: 0.2,
        duration: 500,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  /**
   * 3D 블럭 생성 (입체감 있는 블럭)
   */
  private create3DBlock(x: number, y: number, size: number, color: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    // 색상 계산
    const topColor = this.lightenColor(color, 0.2);
    const rightColor = this.darkenColor(color, 0.1);
    const frontColor = this.darkenColor(color, 0.25);

    const depth = size * 0.4;

    // 오른쪽 면 (평행사변형)
    const rightFace = this.scene.add.graphics();
    rightFace.fillStyle(rightColor, 1);
    rightFace.beginPath();
    rightFace.moveTo(size / 2, 0);
    rightFace.lineTo(size / 2 + depth, -depth);
    rightFace.lineTo(size / 2 + depth, size - depth);
    rightFace.lineTo(size / 2, size);
    rightFace.closePath();
    rightFace.fillPath();
    container.add(rightFace);

    // 윗면 (평행사변형)
    const topFace = this.scene.add.graphics();
    topFace.fillStyle(topColor, 1);
    topFace.beginPath();
    topFace.moveTo(-size / 2, 0);
    topFace.lineTo(-size / 2 + depth, -depth);
    topFace.lineTo(size / 2 + depth, -depth);
    topFace.lineTo(size / 2, 0);
    topFace.closePath();
    topFace.fillPath();
    container.add(topFace);

    // 앞면 (사각형)
    const frontFace = this.scene.add.rectangle(0, size / 2, size, size, frontColor);
    frontFace.setOrigin(0.5, 0.5);
    container.add(frontFace);

    container.setDepth(100);
    return container;
  }

  private lightenColor(color: number, amount: number): number {
    const r = Math.min(255, ((color >> 16) & 0xFF) + 255 * amount);
    const g = Math.min(255, ((color >> 8) & 0xFF) + 255 * amount);
    const b = Math.min(255, (color & 0xFF) + 255 * amount);
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  private darkenColor(color: number, amount: number): number {
    const r = Math.max(0, ((color >> 16) & 0xFF) * (1 - amount));
    const g = Math.max(0, ((color >> 8) & 0xFF) * (1 - amount));
    const b = Math.max(0, (color & 0xFF) * (1 - amount));
    return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
  }

  /**
   * 영역 파괴 효과 - 사망 시 영역이 3D 블럭으로 부서지며 떨어짐
   */
  public territoryShatter(points: { x: number; y: number }[], color: number): void {
    if (!points || points.length < 3) return;

    // 바운딩 박스
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const blockSize = 14;
    const maxBlocks = 30;
    let blockCount = 0;

    // 3D 블럭 생성
    for (let bx = minX; bx < maxX && blockCount < maxBlocks; bx += blockSize * 2.2) {
      for (let by = minY; by < maxY && blockCount < maxBlocks; by += blockSize * 2.2) {
        if (Math.random() > 0.55) continue;

        const px = bx + Phaser.Math.Between(0, blockSize);
        const py = by + Phaser.Math.Between(0, blockSize);

        // 색상 변화
        const variation = Phaser.Math.Between(-15, 15);
        const r = Math.min(255, Math.max(0, ((color >> 16) & 0xFF) + variation));
        const g = Math.min(255, Math.max(0, ((color >> 8) & 0xFF) + variation));
        const b = Math.min(255, Math.max(0, (color & 0xFF) + variation));
        const blockColor = (r << 16) | (g << 8) | b;

        const size = Phaser.Math.Between(blockSize - 3, blockSize + 3);
        const block = this.create3DBlock(px, py, size, blockColor);

        // 바깥 방향으로 튀어나감
        const angle = Math.atan2(py - centerY, px - centerX);
        const speed = Phaser.Math.Between(50, 110);
        const targetX = px + Math.cos(angle) * speed;
        const targetY = py + Math.sin(angle) * speed + Phaser.Math.Between(70, 140);

        const duration = Phaser.Math.Between(500, 750);

        this.scene.tweens.add({
          targets: block,
          x: targetX,
          y: targetY,
          rotation: Phaser.Math.FloatBetween(-2, 2),
          alpha: 0,
          scale: 0.2,
          duration: duration,
          ease: 'Quad.easeOut',
          onComplete: () => block.destroy()
        });

        blockCount++;
      }
    }

    // 작은 파편
    for (let i = 0; i < 12; i++) {
      const px = Phaser.Math.FloatBetween(minX, maxX);
      const py = Phaser.Math.FloatBetween(minY, maxY);

      const debris = this.scene.add.circle(px, py, Phaser.Math.Between(2, 4), color);
      debris.setDepth(99);

      const angle = Math.atan2(py - centerY, px - centerX);
      const speed = Phaser.Math.Between(40, 90);

      this.scene.tweens.add({
        targets: debris,
        x: px + Math.cos(angle) * speed,
        y: py + Math.sin(angle) * speed + Phaser.Math.Between(50, 90),
        alpha: 0,
        scale: 0.2,
        duration: Phaser.Math.Between(400, 550),
        ease: 'Quad.easeOut',
        onComplete: () => debris.destroy()
      });
    }
  }

  /**
   * 컨페티 효과 - 영역 점령 성공 시
   */
  public confetti(x: number, y: number) {
    const colors = [0x4D96FF, 0xFF6B6B, 0x51CF66, 0xFCC419, 0xCC5DE8];
    const particleCount = 20;

    for (let i = 0; i < particleCount; i++) {
      const color = colors[Phaser.Math.Between(0, colors.length - 1)];
      const particle = this.scene.add.rectangle(
        x,
        y,
        Phaser.Math.Between(4, 8),
        Phaser.Math.Between(4, 8),
        color
      );
      particle.setDepth(100);
      particle.setRotation(Phaser.Math.FloatBetween(0, Math.PI * 2));

      const targetX = x + Phaser.Math.Between(-100, 100);
      const targetY = y + Phaser.Math.Between(-150, -50);

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY + 200, // 떨어지는 효과
        rotation: particle.rotation + Phaser.Math.FloatBetween(-2, 2),
        alpha: 0,
        duration: 1000,
        ease: 'Power1',
        onComplete: () => particle.destroy()
      });
    }
  }

  /**
   * 레이어 쌓기 효과
   */
  public layerUp(x: number, y: number, layer: number) {
    const text = this.scene.add.text(x, y - 20, `+${layer}`, {
      fontSize: '16px',
      color: '#FFFFFF',
      stroke: '#2C3E50',
      strokeThickness: 3,
      fontFamily: 'Nunito'
    }).setOrigin(0.5);
    text.setDepth(100);

    this.scene.tweens.add({
      targets: text,
      y: y - 50,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => text.destroy()
    });
  }

  /**
   * 점수 획득 효과
   */
  public scorePopup(x: number, y: number, score: number) {
    const text = this.scene.add.text(x, y, `+${score}`, {
      fontSize: '14px',
      color: '#4D96FF',
      stroke: '#FFFFFF',
      strokeThickness: 2,
      fontFamily: 'Nunito'
    }).setOrigin(0.5);
    text.setDepth(100);

    this.scene.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      scale: 1.2,
      duration: 600,
      ease: 'Power2',
      onComplete: () => text.destroy()
    });
  }

  /**
   * 영역 파헤치기 효과 - 적 영역을 지나갈 때 캐릭터 앞에서 파편이 터져나감
   * @param x 현재 위치 X
   * @param y 현재 위치 Y
   * @param direction 이동 방향 (라디안)
   * @param territoryColor 적 영역 색상
   */
  public debrisSpray(x: number, y: number, direction: number, _territoryColor: number): void {
    const particleCount = 4; // 프레임당 생성되는 파티클 수
    const forwardOffset = 15; // 캐릭터 앞쪽 오프셋

    // 캐릭터 앞쪽 위치 계산
    const frontX = x + Math.cos(direction) * forwardOffset;
    const frontY = y + Math.sin(direction) * forwardOffset;

    for (let i = 0; i < particleCount; i++) {
      // 앞쪽에서 부채꼴로 퍼져나가는 각도 (진행 방향 기준 -60도 ~ +60도)
      const spreadAngle = Phaser.Math.FloatBetween(-Math.PI / 3, Math.PI / 3);
      const angle = direction + spreadAngle;

      const speed = Phaser.Math.Between(40, 100);
      const size = Phaser.Math.Between(3, 7);

      // 대비되는 색상 사용 (흰색 또는 밝은 노란색 랜덤)
      const contrastColors = [0xFFFFFF, 0xFFF9C4, 0xFFECB3, 0xE0E0E0];
      const brightColor = contrastColors[Phaser.Math.Between(0, contrastColors.length - 1)];

      // 작은 사각형 블럭
      const particle = this.scene.add.rectangle(
        frontX + Phaser.Math.Between(-8, 8),
        frontY + Phaser.Math.Between(-8, 8),
        size,
        size,
        brightColor
      );
      particle.setDepth(9);
      particle.setRotation(Phaser.Math.FloatBetween(0, Math.PI));
      particle.setAlpha(0.9);

      // 앞쪽으로 터져나가는 애니메이션 (빠르게 사라짐)
      const targetX = frontX + Math.cos(angle) * speed;
      const targetY = frontY + Math.sin(angle) * speed;

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        rotation: particle.rotation + Phaser.Math.FloatBetween(-2, 2),
        alpha: 0,
        scale: 0.1,
        duration: Phaser.Math.Between(150, 300), // 빠르게 사라짐
        ease: 'Power3',
        onComplete: () => particle.destroy()
      });
    }
  }

  /**
   * 부스터 효과 - 캐릭터 뒤로 화려한 꽃가루가 날림
   * @param x 현재 위치 X
   * @param y 현재 위치 Y
   * @param direction 이동 방향 (라디안)
   * @param color 캐릭터 색상
   */
  public boostTrail(x: number, y: number, direction: number, color: number): void {
    const particleCount = 3;
    const backOffset = 15; // 캐릭터 뒤쪽 오프셋

    // 캐릭터 뒤쪽 위치
    const backX = x - Math.cos(direction) * backOffset;
    const backY = y - Math.sin(direction) * backOffset;

    // 화려한 색상들
    const sparkleColors = [
      0xFFD700, // 금색
      0xFF69B4, // 핫핑크
      0x00FFFF, // 시안
      0xFF6B6B, // 빨강
      0x51CF66, // 초록
      0xCC5DE8, // 보라
      0xFFFFFF, // 흰색
      color,    // 캐릭터 색상
    ];

    for (let i = 0; i < particleCount; i++) {
      // 뒤쪽으로 퍼지는 각도
      const spreadAngle = Phaser.Math.FloatBetween(-Math.PI / 3, Math.PI / 3);
      const angle = direction + Math.PI + spreadAngle; // 반대 방향

      const speed = Phaser.Math.Between(30, 80);
      const size = Phaser.Math.Between(3, 8);
      const sparkleColor = sparkleColors[Phaser.Math.Between(0, sparkleColors.length - 1)];

      // 다양한 모양 (원, 사각형, 별 모양)
      const shapeType = Phaser.Math.Between(0, 2);
      let particle: Phaser.GameObjects.Shape;

      if (shapeType === 0) {
        // 원형
        particle = this.scene.add.circle(
          backX + Phaser.Math.Between(-5, 5),
          backY + Phaser.Math.Between(-5, 5),
          size / 2,
          sparkleColor
        );
      } else if (shapeType === 1) {
        // 사각형
        particle = this.scene.add.rectangle(
          backX + Phaser.Math.Between(-5, 5),
          backY + Phaser.Math.Between(-5, 5),
          size,
          size,
          sparkleColor
        );
        particle.setRotation(Phaser.Math.FloatBetween(0, Math.PI));
      } else {
        // 다이아몬드 (회전된 사각형)
        particle = this.scene.add.rectangle(
          backX + Phaser.Math.Between(-5, 5),
          backY + Phaser.Math.Between(-5, 5),
          size,
          size * 1.5,
          sparkleColor
        );
        particle.setRotation(Math.PI / 4);
      }

      particle.setDepth(8);
      particle.setAlpha(0.9);

      // 뒤쪽으로 날아가며 사라지는 애니메이션
      const targetX = backX + Math.cos(angle) * speed;
      const targetY = backY + Math.sin(angle) * speed;

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY + Phaser.Math.Between(5, 15), // 약간 아래로 떨어짐
        rotation: particle.rotation + Phaser.Math.FloatBetween(-2, 2),
        alpha: 0,
        scale: Phaser.Math.FloatBetween(0.3, 0.8),
        duration: Phaser.Math.Between(200, 400),
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

}
